import type { QueryClient } from "@tanstack/react-query";
import type { RealtimeClient } from "@cntrl/realtime";
import { resetAllStores } from "./stores.js";

export interface SwitchBandDeps {
  queryClient: QueryClient;
  /** The single realtime connection (null before Phase 1 wires a producer). */
  realtime?: RealtimeClient | null;
  /** Router navigation to the new band context. */
  navigate: (bandId: string) => void | Promise<void>;
}

/**
 * switchBand — the SINGLE audited scorch path. The MVP's roster shell did
 * NOT scorch (repaint only → the cross-artist cache bleed); this is the structural fix. Order:
 *   (1) abort in-flight requests   (2) leave band-scoped SignalR groups   (3) queryClient.clear()
 *   (4) reset every Zustand store   (5) navigate. (Push is not re-registered: a device registers for the
 *   PERSON, not a band — ART-259 — so a band switch changes nothing about where notifications arrive.)
 * Leak tests 2/4 assert this mechanism — every tenant-marked value changes or empties, and the
 * DOM shares nothing across a switch.
 *
 * NOTE on step (2): the hub connection is PERSON-bound, so a band switch leaves only the band-scoped
 * groups — done declaratively by useLiveGroup(`band:{id}`) reacting to the bandId change (the person `user:{id}`
 * group and the connection itself correctly persist). A `realtime` client is passed ONLY for a full teardown
 * (it stops the whole connection); the band-switch caller passes none.
 */
export async function switchBand(newBandId: string, deps: SwitchBandDeps): Promise<void> {
  // (1) abort in-flight requests — cancel every running query so a late response can't paint stale tenant data.
  await deps.queryClient.cancelQueries();

  // (2) full teardown ONLY when a client is passed (logout-style). A band switch passes none — see the note
  // above: band-scoped group leave is declarative, the person-bound connection persists.
  if (deps.realtime) {
    await deps.realtime.stop();
  }

  // (3) clear the entire query cache — NEVER show another band's cached data (the fix).
  deps.queryClient.clear();

  // (4) reset every Zustand UI store.
  resetAllStores();

  // (5) navigate to the new band context (the shell refetches /me/navigation?context=band:{newBandId}).
  await deps.navigate(newBandId);
}
